export const MONTHLY_THREAD_SCALE = 10;

function finiteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function allowancePair(ai = {}) {
  const microBudget = finiteNumber(ai.budgetMicrousd);
  const microRemaining = finiteNumber(ai.remainingMicrousd);
  if (microBudget !== null && microBudget > 0) {
    const spent = finiteNumber(ai.spentMicrousd) || 0;
    const reserved = finiteNumber(ai.reservedMicrousd) || 0;
    return {
      budget: microBudget,
      remaining: microRemaining ?? Math.max(0, microBudget - spent - reserved),
    };
  }

  const budget = finiteNumber(ai.budgetUsd);
  const remaining = finiteNumber(ai.remainingUsd);
  if (budget !== null && budget > 0) {
    return {
      budget,
      remaining: remaining ?? Math.max(0, budget - (finiteNumber(ai.spentUsd) || 0)),
    };
  }

  return null;
}

export function getThreadUsage(ai = {}) {
  if (ai?.trial) {
    const remaining = Math.max(0, Number(ai.dailyTryOnRemaining) || 0);
    return {
      mode: 'finite',
      threadsLeft: remaining,
      fillPercent: remaining * 100,
      valueLabel: remaining === 1 ? '1 Try-On left today' : 'No Try-On left today',
    };
  }
  if (ai?.unlimited) {
    return {
      mode: 'open',
      threadsLeft: null,
      fillPercent: 100,
      valueLabel: 'Threads ready',
    };
  }

  const allowance = allowancePair(ai);
  if (!allowance) {
    return {
      mode: 'unavailable',
      threadsLeft: null,
      fillPercent: 0,
      valueLabel: 'Usage is refreshing',
    };
  }

  const threadScale = MONTHLY_THREAD_SCALE * Math.max(1, Number(ai?.promotion?.multiplier) || 1);
  const ratio = clamp(allowance.remaining / allowance.budget, 0, 1);
  const threadsLeft = ratio === 0
    ? 0
    : Math.max(1, Math.ceil(ratio * threadScale));
  const noun = threadsLeft === 1 ? 'Thread' : 'Threads';

  return {
    mode: 'finite',
    threadsLeft,
    fillPercent: (threadsLeft / threadScale) * 100,
    valueLabel: threadsLeft === 0
      ? 'No Threads left this month'
      : `${threadsLeft} ${noun} left this month`,
  };
}

export function getStorageUsage(quota = {}, fallbackQuota = null) {
  if (quota?.unlimited || Number(quota?.quota ?? fallbackQuota) < 0) {
    return {
      mode: 'open',
      availablePercent: null,
      fillPercent: 100,
      valueLabel: 'Open media room',
    };
  }

  const limit = finiteNumber(quota?.quota ?? fallbackQuota);
  const used = finiteNumber(quota?.used);
  const reportedRemaining = finiteNumber(quota?.remaining);
  if (limit === null || limit <= 0 || (used === null && reportedRemaining === null)) {
    return {
      mode: 'unavailable',
      availablePercent: null,
      fillPercent: 0,
      valueLabel: 'Capacity is refreshing',
    };
  }

  const remaining = clamp(reportedRemaining ?? limit - used, 0, limit);
  const availablePercent = Math.round((remaining / limit) * 100);
  let valueLabel = 'Full';
  if (availablePercent >= 71) valueLabel = 'Plenty of room';
  else if (availablePercent >= 31) valueLabel = 'Room to grow';
  else if (availablePercent >= 11) valueLabel = 'Getting snug';
  else if (availablePercent >= 1) valueLabel = 'Almost full';

  return {
    mode: 'finite',
    availablePercent,
    fillPercent: availablePercent,
    valueLabel,
  };
}

export function nextMonthlyResetLabel(ai = {}) {
  const explicitReset = Date.parse(ai?.resetAt || '');
  let reset = Number.isFinite(explicitReset) ? new Date(explicitReset) : null;

  if (!reset) {
    const match = /^(\d{4})-(\d{2})$/.exec(String(ai?.period || ''));
    if (match) reset = new Date(Date.UTC(Number(match[1]), Number(match[2]), 1));
  }

  if (!reset) {
    const now = new Date();
    reset = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  }

  return reset.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function compactThreadLabel(ai = {}) {
  const usage = getThreadUsage(ai);
  if (usage.mode === 'open') return 'Threads ready';
  if (usage.mode === 'unavailable') return 'Monthly Threads are refreshing';
  return usage.valueLabel;
}
